/**
 * Listening-session telemetry (reliability brief, Oct 28 2026). Mount once in AppLayout.
 *
 * Observe-only: subscribes to the Zustand store and the diagnostic log, never calls
 * QueueController / TransportCommand and never writes the store. Events ride the existing
 * personalization batch (/api/valet/events) with the Connector version on THIS machine at
 * the time of the event. Works with every Connector version (reads web-side state only).
 */
import { useEffect } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import { trackEvent, flushPersonalizationEvents } from './usePersonalization';
import { observeDiagnosticEvents } from '../utils/diagnosticLogger';
import { getConnectorVersion } from '../utils/connectorVersion';
import { getConnectorMachineIdHash } from '../utils/connectorMachineIdHash';
import { ListeningSessionTracker } from '../services/listeningSessionTracker';
import { OPEN_SESSION_KEY, writeStored, takeOrphanedEnd } from '../services/listeningSessionStore';
import { platformToken, webVersion, sessionContext, rendererKind } from '../services/listeningSessionMeta';

export { rendererKind };
import { msSinceUserTransportCommand } from '../transport/transportUserCommand';

const USER_CMD_WINDOW_MS = 4_000; const TICK_MS = 5_000;
export { OPEN_SESSION_KEY };

let machineHash = null;

export default function useListeningSessionTelemetry() {
  useEffect(() => {
    const tracker = new ListeningSessionTracker();
    let lastUserCmdAt = 0;
    void getConnectorMachineIdHash().then((h) => { machineHash = h; }).catch(() => {});

    const versionMeta = () => ({
      connector_version: getConnectorVersion() || null,
      machine_id_hash: machineHash,
      web_version: webVersion(),
      platform: platformToken(),
    });
    const send = (events, meta) => {
      for (const e of events) {
        trackEvent(e.type, { entityType: 'listening_session', metadata: { ...e.metadata, ...(meta || versionMeta()) } });
      }
    };
    // Keep the stored copy of the open session current; remove it once the session has ended and been sent.
    const persist = (now = Date.now()) => {
      const snap = tracker.snapshot(now);
      writeStored(snap ? { ...snap, meta: versionMeta() } : null);
    };

    // A session left open by a window that closed, slept or reloaded: send its end now, with the versions it ran on.
    const orphan = takeOrphanedEnd(Date.now());
    if (orphan) {
      void getConnectorMachineIdHash().catch(() => null).then((h) => {
        send([orphan.event], { ...orphan.meta, machine_id_hash: orphan.meta.machine_id_hash || h || null });
        void flushPersonalizationEvents();
      });
    }

    const observe = (s, now = Date.now()) => tracker.observe({
      playbackState: s.playbackState,
      trackId: s.currentTrack ? String(s.currentTrack.qobuzTrackId || s.currentTrack.id || s.currentTrack.file_path || '') || null : null,
      position: Number(s.position) || 0,
      duration: Number(s.duration) || Number(s.currentTrack?.duration) || 0,
      queueIndex: Number(s.queueIndex) || 0,
      queueLength: Array.isArray(s.queue) ? s.queue.length : 0,
      repeatMode: s.repeatMode || 'off',
      userCommandRecent: Math.min(now - lastUserCmdAt, msSinceUserTransportCommand()) <= USER_CMD_WINDOW_MS,
    }, sessionContext, now);

    const offDiag = observeDiagnosticEvents((ev) => {
      try {
        const msg = String(ev?.message || '');
        const now = Date.now();
        if (msg.startsWith('Auto-advance from')) {
          const reason = ev?.payload?.reason;
          if (reason) tracker.noteAdvanceReason(String(reason), now); if (reason === 'skip') lastUserCmdAt = now;
        } else if (msg === 'playFrom entry') {
          tracker.noteUserPlayIntent(now); lastUserCmdAt = now;
        } else if (msg.startsWith('Command dispatched: seek')) {
          lastUserCmdAt = now; // seeks only come from the listener; play/stop lines also fire on auto-advance
        }
      } catch { /* telemetry never throws */ }
    });

    const unsub = useMaestroStore.subscribe((s) => {
      try { const ev = observe(s); send(ev); if (ev.length) persist(); } catch { /* never throws */ }
    });
    const timer = setInterval(() => {
      try { send(tracker.tick(sessionContext, Date.now())); persist(); } catch { /* never throws */ }
    }, TICK_MS);
    // beforeunload can be cancelled, so it only refreshes the stored copy; pagehide is the real close.
    const onBeforeUnload = () => { try { persist(); } catch { /* ignore */ } };
    const onUnload = () => {
      try {
        const now = Date.now();
        const snap = tracker.snapshot(now);
        if (snap) writeStored({ ...snap, meta: versionMeta(), closing: true }); // stays stored until the next launch confirms it, in case this send is lost
        const ev = tracker.unload(now);
        if (ev.length) { send(ev); void flushPersonalizationEvents(); }
      } catch { /* ignore */ }
    };
    const onHidden = () => { if (document.visibilityState === 'hidden') onBeforeUnload(); };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onUnload);
    document.addEventListener('visibilitychange', onHidden);

    return () => {
      offDiag();
      unsub();
      clearInterval(timer);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onUnload);
      document.removeEventListener('visibilitychange', onHidden);
    };
  }, []);
}
